"use strict";
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.World = void 0;
exports.createSystem = createSystem;
var ECSv2_1 = require("./ECSv2");
var World = /** @class */ (function () {
    function World(maxEntities, components, worldWidth, worldHeight) {
        this.tickMs = 500;
        this.maxActiveId = 0;
        this.lastTickTime = 0;
        this.rules = [];
        this.ticks = 0;
        this.global = {};
        this.ecs = new ECSv2_1.ECS(maxEntities, components);
        this.ecs.worldHeight = worldHeight;
        this.ecs.worldWidth = worldWidth;
        this.lastTickTime = performance.now();
        this.setupGameLoop();
        console.log("World initialized with tick rate: ".concat(this.tickMs, "ms"));
    }
    World.prototype.addRule = function (name, description, fn) {
        if (name in this.ecs.data &&
            this.ecs.getComponentType(name) !== "boolean") {
            throw new Error("Rule '".concat(name, "' requires a boolean component type, but it already exists with a different type. Rules must be defined before they are used."));
        }
        this.ecs.addComponent(name, "boolean");
        this.rules.push({
            name: name,
            description: description,
            fn: createSystem(this, fn),
        });
    };
    World.prototype.calculateMaxActiveId = function () {
        var activeArray = this.ecs.data["active"];
        // Find the last active entity by traversing the array in reverse
        for (var i = activeArray.length - 1; i >= 0; i--) {
            if (activeArray[i] === true) {
                return i;
            }
        }
        return -1;
    };
    World.prototype.addEntity = function (data) {
        var firstUnusedIndex = -1;
        var activeArray = this.ecs.data["active"];
        for (var i = 0; i < activeArray.length; i++) {
            if (activeArray[i] === false) {
                firstUnusedIndex = i;
                break;
            }
        }
        if (firstUnusedIndex === -1) {
            console.warn("No free entity slots available");
            return -1;
        }
        // For each component array in the ECS
        for (var componentName in this.ecs.data) {
            var componentArray = this.ecs.data[componentName];
            if (componentName in data) {
                // Set the value from the provided data
                if (componentArray instanceof Int32Array ||
                    componentArray instanceof Float32Array) {
                    componentArray[firstUnusedIndex] = data[componentName];
                }
                else if (Array.isArray(componentArray)) {
                    if (this.ecs.getComponentType(componentName) === "boolean") {
                        // Boolean array
                        componentArray[firstUnusedIndex] = Boolean(data[componentName]);
                    }
                    else {
                        // String array
                        componentArray[firstUnusedIndex] = String(data[componentName]);
                    }
                }
            }
            else {
                // Reset to default value
                if (componentArray instanceof Int32Array ||
                    componentArray instanceof Float32Array) {
                    componentArray[firstUnusedIndex] = 0;
                }
                else if (Array.isArray(componentArray)) {
                    if (this.ecs.getComponentType(componentName) === "boolean") {
                        // Boolean array
                        componentArray[firstUnusedIndex] = false;
                    }
                    else {
                        // String array
                        componentArray[firstUnusedIndex] = "";
                    }
                }
            }
        }
        activeArray[firstUnusedIndex] = data["active"] === false ? false : true;
        // Update max active ID if needed
        if (firstUnusedIndex > this.maxActiveId) {
            this.maxActiveId = firstUnusedIndex;
        }
        return firstUnusedIndex;
    };
    World.prototype.tick = function () {
        var currentTime = performance.now();
        var deltaTime = currentTime - this.lastTickTime;
        this.lastTickTime = currentTime;
        // Run each enabled rule
        for (var _i = 0, _a = this.rules; _i < _a.length; _i++) {
            var rule = _a[_i];
            var startTime = performance.now();
            rule.fn(this);
            var endTime = performance.now();
            if (endTime - startTime > 50) {
                console.warn("Rule ".concat(rule.name, " took ").concat((endTime - startTime).toFixed(2), "ms to execute"));
            }
        }
        this.ticks = this.ticks + 1;
        this.maxActiveId = this.calculateMaxActiveId();
        // Update the ECS tick count and elapsed time
        this.ecs.incrementTick(deltaTime);
    };
    World.prototype.setTickRate = function (ms) {
        if (ms > 0) {
            this.tickMs = ms;
            console.log("Tick rate set to ".concat(ms, "ms"));
            // Restart game loop with new tick rate
            this.setupGameLoop();
        }
    };
    /**
     * Disable automatic ticking - useful for tests that need manual control
     */
    World.prototype.disableAutoTick = function () {
        if (this.tickInterval !== undefined) {
            clearInterval(this.tickInterval);
            this.tickInterval = undefined;
            console.log("Automatic ticking disabled");
        }
    };
    /**
     * Enable automatic ticking with the current tick rate
     */
    World.prototype.enableAutoTick = function () {
        this.setupGameLoop();
        console.log("Automatic ticking enabled");
    };
    World.prototype.setupGameLoop = function () {
        var _this = this;
        // Clear any existing interval
        if (this.tickInterval !== undefined) {
            clearInterval(this.tickInterval);
        }
        this.tickInterval = window.setInterval(function () { return _this.tick(); }, this.tickMs);
    };
    // Clean up resources when no longer needed
    World.prototype.dispose = function () {
        if (this.tickInterval !== undefined) {
            clearInterval(this.tickInterval);
            this.tickInterval = undefined;
        }
    };
    return World;
}());
exports.World = World;
function createSystem(world, fn) {
    var _a;
    // Extract parameter names after first (world)
    var paramNames = ((_a = fn
        .toString()
        .replace(/\/\*[\s\S]*?\*\/|([^\\:]|^)\/\/.*$/gm, "")
        .match(/\((.*?)\)/)) === null || _a === void 0 ? void 0 : _a[1].split(",").map(function (p) { return p.trim().split(":")[0]; }).slice(1)) || [];
    // Capture current component arrays from ECS
    var components = paramNames.map(function (name) {
        var component = world.ecs.data[name];
        if (!component) {
            throw new Error("Missing component array for ".concat(name));
        }
        return component;
    });
    // Return function that uses captured components but receives fresh world
    return function (currentWorld) {
        fn.apply(void 0, __spreadArray([currentWorld], components, false));
    };
}
